import { useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import {
  isPairingOption, MOVE_OPTION_CARDS, moveOptionChanges, NEXT_REGISTER_WEAPONS, pairProblem,
  PUSH_OPTIONS, REGISTER_WEAPONS, SIDED_TURN_CARDS, TURN_WEAPONS, virtualRegisters,
} from '@roborally/engine';
import type { OptionEffect } from '@roborally/engine';
import type { ProgramCard, OptionCardView, PairedRegister, RelativeFacing } from '../api.js';
import { PowerDownChoice } from './PowerDownChoice.js';
import { CARD_LABEL, OPTION_SLOT_NAME } from '../cardLabels.js';
import { AIM_SIDES, AIM_GLYPH, AIM_LABEL } from '../aimLabels.js';
import { optionTooltip } from '../optionPayload.js';

/**
 * The programming interface: your hand below, five register slots above.
 *
 * Drag and drop, with a click fallback. The fallback is not decoration:
 * HTML5 drag and drop does not work on touch devices at all, and it is
 * awkward for anyone using a keyboard. Click a card, click a slot does the
 * same job and costs about ten lines.
 *
 * Locked registers are shown but not interactive. They already hold the
 * card they will execute again this turn, placed either by last turn's
 * programming or by the Deal's random fill.
 */

/** The drag type a card dragged out of a register carries: its slot. */
const FROM_SLOT = 'application/x-rallyx-slot';

const FACING_LABEL: Record<string, string> = {
  N: '\u2191 North', E: '\u2192 East', S: '\u2193 South', W: '\u2190 West',
};

/** What an Option has left to spend, after its name: " [3]". */
function Charges({ option }: { option: OptionCardView }) {
  return option.remaining === undefined
    ? null
    : <span className="option-payload num"> [{option.remaining}]</span>;
}

/** Which way a robot faces: on its first turn, and coming back from
 * destruction. */
export function FacingPick({ facing, onPick, disabled }: {
  facing?: 'N' | 'E' | 'S' | 'W';
  onPick: (facing: 'N' | 'E' | 'S' | 'W') => void;
  disabled: boolean;
}) {
  return (
    <div className="facing-pick">
      <p className="eyebrow">Which way does your robot face to start?</p>
      <div className="row">
        {(['N', 'E', 'S', 'W'] as const).map((d) => (
          <button
            key={d}
            className={facing === d ? '' : 'secondary'}
            onClick={() => onPick(d)}
            disabled={disabled}
          >
            {FACING_LABEL[d]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Whether a robot powered down last turn, or back from destruction, is
 * powered down for this one: asked before the hand is seen. */
export function PowerPick({ onPick, disabled }: {
  onPick: (poweredDown: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="facing-pick">
      <p className="eyebrow">Powered down this turn?</p>
      <div className="row">
        <button
          onClick={() => onPick(true)}
          disabled={disabled}
          title="Takes no actions this turn and repairs all damage. The answer if the clock runs out"
        >
          Stay powered down
        </button>
        <button className="secondary" onClick={() => onPick(false)} disabled={disabled}>
          Power up
        </button>
      </div>
    </div>
  );
}

/** Which Option a robot back from destruction gives up: asked before the
 * hand is seen. */
export function OptionLossPick({ options, onPick, disabled }: {
  options: OptionCardView[];
  onPick: (optionId: string) => void;
  disabled: boolean;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  return (
    <div className="facing-pick">
      <p className="eyebrow">Your robot was destroyed. Which option do you give up?</p>
      <div className="option-tray">
        {/* Keyed by POSITION as well as id: a hand that holds two of the
            same card would otherwise give React two chips with one key. */}
        {options.map((option, i) => (
          <button
            key={`${option.id}-${i}`}
            className={chosen === option.id ? 'option-chip chosen' : 'option-chip'}
            onClick={() => setChosen(option.id)}
            disabled={disabled}
            title={option.text}
          >
            {option.name}
          </button>
        ))}
      </div>
      <div className="row">
        <button
          onClick={() => chosen && onPick(chosen)}
          disabled={disabled || !chosen}
          title="One goes at random if the clock runs out"
        >
          Give it up
        </button>
      </div>
    </div>
  );
}

/**
 * The four ways an aimed Option can be turned, in the order they are
 * offered: front first, since that is where an untouched card points.
 *
 * Initials rather than arrows. An arrow reads as a compass direction on a
 * screen with a board on it, and these are sides OF THE ROBOT -- F is
 * whichever way the robot happens to be facing. The tooltip spells each
 * one out.
 */
const CARD_GLYPH: Record<string, string> = {
  UTurn: '\u21BA',
  RotateLeft: '\u21B0',
  RotateRight: '\u21B1',
  BackUp: '\u2193',
  Move1: '\u2191',
  Move2: '\u2191\u2191',
  Move3: '\u2191\u2191\u2191',
};

/**
 * Something a robot won at a chop shop, a repair site or in radioactive
 * waste, named so it can be reported where and when it happened.
 *
 * `taken` is an outright grant, `swapped` a chop shop exchange -- the
 * player gave something up for it, so it reads differently -- and
 * `reloaded` a launcher refilled, which is neither but is the third
 * thing a chop shop does and belongs in the same report.
 */
export interface Award {
  name: string;
  kind: 'taken' | 'swapped' | 'reloaded';
  /** What was given up, on a swap that named it. */
  gaveUp?: string;
}

interface ProgrammerProps {
  hand: ProgramCard[];
  locked: boolean[];
  /** Cards already in registers, including locked ones. */
  registers: (ProgramCard | null)[];
  onChange: (registers: (ProgramCard | null)[]) => void;
  onSubmit: () => void;
  /**
   * What this robot won at register 5 and at the end of the turn just
   * played. Registers 1 to 4 report themselves at the pause that follows
   * them, so what reaches here is only what has not been said yet.
   */
  gained: Award[];
  /** Options that can hold a card, and gadgets that can go in a register.
   * Empty in a game without Options, which is when none of this shows. */
  options: OptionCardView[];
  /**
   * Cards set aside on Options, one entry per Option holding one.
   *
   * A list rather than a single entry: a robot with both a Conditional
   * Program and a Flywheel can feed both in one turn if it has the cards
   * to spare, and a single slot made them mutually exclusive.
   */
  setAside: { optionId: string; card: ProgramCard }[];
  onSetAsideChange: (next: { optionId: string; card: ProgramCard }[]) => void;
  /** Gadgets programmed into each register, by Option id. */
  gadgets: string[][];
  onGadgetsChange: (next: string[][]) => void;
  /** Turn mode's Fourth Gear, Brakes and Reverse Gears programmed into
   * each register, by Option id. Any number of registers, and beside a
   * gadget. */
  moveOptions: string[][];
  onMoveOptionsChange: (next: string[][]) => void;
  /** Registers programmed with an Option that pairs cards: the Option,
   * and the second card beside the one in `registers`. */
  paired: (PairedRegister | null)[];
  onPairedChange: (next: (PairedRegister | null)[]) => void;
  /**
   * Turn-programmed Options switched on for this turn, by Option id.
   *
   * Belongs to the turn rather than to a register, and consumes nothing:
   * the player is only saying "this one applies this turn". Some are
   * worth leaving OFF -- board rotation is often helpful, so a
   * Gyroscopic Stabilizer is not a card you want on every turn.
   */
  activated: string[];
  onActivatedChange: (next: string[]) => void;
  /**
   * Which way each aimed Option is turned for this turn, by Option id.
   *
   * The Shield, the Reflector and the Hydraulic Bumpers are turned rather
   * than switched: they are always working, and the programming decision
   * is which of the robot's four sides they cover. Turned once for the
   * turn, not once per register. An Option with no entry faces front.
   */
  aimed: { optionId: string; facing: RelativeFacing }[];
  onAimedChange: (next: { optionId: string; facing: RelativeFacing }[]) => void;
  /**
   * The Recompile card, when this robot holds one and has not spent it
   * this turn.
   *
   * A card rather than a flag, so the row can carry the wording in its
   * tooltip like every other Option here. Undefined means there is
   * nothing to offer -- no card, or already recompiled.
   */
  recompile?: OptionCardView;
  onRecompile: () => void;
  submitting: boolean;
  submitted: boolean;
  /** Takes the program back so it can be changed and sent again. Offered
   * only once submitted, and the server refuses it once the turn has
   * started -- which is the moment the last player's program lands. */
  onUnsubmit: () => void;
  /** The red lines saying what robots did to each other, drawn directly
   * above the registers. */
  notices?: ReactNode;
  powerDown: boolean;
  onPowerDownChange: (value: boolean) => void;
  /**
   * Whether this robot is virtual as programming closes.
   *
   * A note on the buttons, and a bar only on the registers it is certain
   * to be virtual for still (`virtualRegisters`): register 1, and on turn
   * 1 all of them. Past those it may separate, and the engine decides at
   * deploy time, where a drop attempted while virtual fizzles with its
   * payload intact (game.ts, deployGadgets).
   */
  virtual: boolean;
  /** The turn, for `virtualRegisters`. */
  turnNumber: number;
  /** Turn 1 only: the player chooses which way their robot faces, and it
   * travels inside this same submission. RULES_SPEC section 2. */
  needsFacing: boolean;
  facing: 'N' | 'E' | 'S' | 'W';
  onFacingChange: (facing: 'N' | 'E' | 'S' | 'W') => void;
  /** Caught by a scrambler bomb: no hand and nothing to place, but the
   * power-down choice is still made and submitted here. */
  scrambled: boolean;
  /** The sides a scrambler bomb turned the aimed Options to: shown, and
   * not the player's to change. */
  bombAims: { optionId: string; facing: RelativeFacing }[];
  /** The switches a scrambler bomb set on, likewise: every other one it
   * set off. */
  bombActivated: string[];
}

export function Programmer({
  hand, locked, registers: given, onChange, onSubmit, submitting, submitted, onUnsubmit, notices, gained,
  powerDown, onPowerDownChange,
  needsFacing, facing, onFacingChange,
  options, setAside, onSetAsideChange, gadgets, onGadgetsChange, moveOptions, onMoveOptionsChange,
  paired, onPairedChange,
  activated, onActivatedChange, aimed, onAimedChange, virtual, turnNumber,
  recompile, onRecompile, scrambled, bombAims, bombActivated,
}: ProgrammerProps) {
  const [held, setHeld] = useState<ProgramCard | null>(null);
  const stillVirtual = virtualRegisters({ virtual }, turnNumber);

  // Extra Register's register 6, after the five, while the robot holds the
  // card. Optional: left empty, it is not played. The bomb fills five and
  // leaves no card to play there.
  const extra = !scrambled && options.some((o) => o.effect === 'extraRegister');
  const registers = extra ? [...given.slice(0, 5), given[5] ?? null] : given.slice(0, 5);
  const slots = registers.map((_, i) => i);

  // Options that hold a program card, and gadgets that go into a register.
  // Cards still to be placed in registers. A card can only be set aside
  // if there is one spare after programming -- offering the Flywheel with
  // exactly five cards left is offering an impossible choice.
  // A pair takes two, and Overload Override's empty register none.
  const toFill = locked.reduce((n, l, i) => {
    if (l) return n;
    const pair = paired[i];
    if (!pair) return n + 1;
    return n + (pair.card ? 2 : registers[i] ? 1 : isOverride(pair.via) ? 0 : 2);
  }, 0);
  // Register 6's cards, which the five above do not count.
  const spare = hand.length - toFill - (registers[5] ? 1 : 0) - (paired[5]?.card ? 1 : 0);

  /** Whether this register is done: a card, a pair, or Overload
   * Override's none. */
  function filled(i: number): boolean {
    if (i === 5) return true;
    if (locked[i]) return true;
    const pair = paired[i];
    if (!pair) return registers[i] !== null;
    const count = (registers[i] ? 1 : 0) + (pair.card ? 1 : 0);
    return count === 2 || (count === 0 && isOverride(pair.via));
  }
  function isOverride(optionId: string): boolean {
    return options.some((o) => o.id === optionId && o.effect === 'overloadOverride');
  }

  /** Register 6 only behind a full five: a register among them emptied
   * again sends register 6's cards back to the hand. */
  function guardSixth(next: (ProgramCard | null)[], nextPaired: (PairedRegister | null)[]): void {
    if (!next[5] && !nextPaired[5]?.card) return;
    const done = [0, 1, 2, 3, 4].every((i) => {
      if (locked[i]) return true;
      const pair = nextPaired[i];
      if (!pair) return next[i] !== null;
      const count = (next[i] ? 1 : 0) + (pair.card ? 1 : 0);
      return count === 2 || (count === 0 && isOverride(pair.via));
    });
    if (done) return;
    next[5] = null;
    if (nextPaired[5]) nextPaired[5] = { via: nextPaired[5]!.via };
  }

  // "AFTER programming your registers, you may place one of the remaining
  // program cards on this option" -- the Conditional, and the Flywheel
  // says "after programming all five registers". Both mean what they say,
  // and offering the choice earlier was not merely early: a card set
  // aside is off the table, so if the timer then ran out it was neither
  // in a register nor available to fill one, and the player had put it
  // somewhere they could not see it.
  //
  // A register the player empties again takes the row away with it. The
  // card already set aside is left alone rather than snatched back --
  // swapping one card for another passes through "not programmed", and
  // undoing the player's other decision on the way would be its own
  // surprise.
  const programmed = registers.every((_, i) => filled(i));

  const holders = options.filter(
    (o) => (o.effect === 'conditional' || o.effect === 'flywheel')
      && spare > 0 && programmed,
  );
  // Not an empty one: a launcher with nothing left drops nothing, so it is
  // not offered. It is still held, and a chop shop can reload it.
  const registerWeapon = (o: OptionCardView) => REGISTER_WEAPONS.has(o.effect as OptionEffect);
  const isMoveOption = (o: OptionCardView) => o.turnMode === true
    && (o.effect in MOVE_OPTION_CARDS || registerWeapon(o));
  // Scrambled, only the turn cards whose originals are run-time choices:
  // dropping, pairing and the rest are programming, and the bomb did it.
  const launchers = scrambled ? [] : options.filter((o) => o.timing === 'phaseProgrammed' && !isMoveOption(o)
    && !isPairingOption(o)
    && !(o.payload !== undefined && (o.remaining ?? 0) <= 0));
  // Options that put a second card in a register -- or, for Overload
  // Override, none. One a register: choosing another there replaces it.
  const pairers = scrambled ? [] : options.filter((o) => isPairingOption(o));
  const togglePair = (slot: number, optionId: string) => {
    const next = slots.map((i) => paired[i] ?? null);
    const here = next[slot];
    if (here?.via === optionId) {
      // Off: the second card goes back to the hand.
      next[slot] = null;
    } else {
      // On, keeping a second card the new Option still takes.
      const option = options.find((o) => o.id === optionId)!;
      const cards = [registers[slot], here?.card].filter((c): c is ProgramCard => !!c);
      const keep = here?.card && cards.length === 2 && !pairProblem(option, cards);
      next[slot] = keep ? { via: optionId, card: here!.card } : { via: optionId };
      // No manoeuvre goes in a pair, and no gadget in Overload Override.
      if ((moveOptions[slot] ?? []).some((id) => !weaponIds.has(id))) {
        onMoveOptionsChange(slots.map((i) => (
          i === slot ? (moveOptions[i] ?? []).filter((id) => weaponIds.has(id)) : (moveOptions[i] ?? [])
        )));
      }
      if (option.effect === 'overloadOverride' && (gadgets[slot] ?? []).length > 0) {
        onGadgetsChange(slots.map((i) => (i === slot ? [] : (gadgets[i] ?? []))));
      }
    }
    // A pair switched on wants a second card, so the five may no longer
    // be full.
    const regs = registers.slice();
    guardSixth(regs, next);
    if (regs[5] !== registers[5]) onChange(regs);
    onPairedChange(next);
  };
  /** Why a drop onto a register was refused, shown on that register --
   * on its second row when `second`. */
  const [refused, setRefused] = useState<{ slot: number; second?: boolean; why: string } | null>(null);
  // Turn mode's move options: phase-programmed too, but they deploy
  // nothing, so each goes in as many registers as the player likes, beside
  // a gadget. One a register: choosing another there replaces it. The Big
  // Gun is a weapon rather than a change to the card, and goes beside one.
  const moveOptionCards = options.filter(isMoveOption);
  const weaponIds = new Set(options.filter(registerWeapon).map((o) => o.id));
  const toggleMoveOption = (slot: number, optionId: string) => {
    const next = slots.map((i) => (moveOptions[i] ?? []).slice());
    const here = next[slot];
    next[slot] = here.includes(optionId)
      ? here.filter((id) => id !== optionId)
      : [...here.filter((id) => weaponIds.has(id) !== weaponIds.has(optionId)), optionId];
    onMoveOptionsChange(next);
  };

  // Turn-programmed Options that are switched on rather than fed a card.
  // The Flywheel is turn-programmed too but works by holding a card, so
  // it is already offered under "Set a card aside" and must not appear
  // here as well.
  const switches = options.filter(
    (o) => o.timing === 'turnProgrammed' && o.effect !== 'flywheel'
      && o.effect !== 'extraRegister' && !o.directional,
  );
  // Scrambled, a switch that is not a turn card is the bomb's.
  const bombSet = (o: OptionCardView) => scrambled && o.turnMode !== true;

  // Turn-programmed Options that are TURNED rather than switched: the
  // card works either way and what is programmed is the side it covers.
  const bombSide = new Map(scrambled ? bombAims.map((a) => [a.optionId, a.facing]) : []);
  const dials = options.filter((o) => o.directional);

  // A turn-mode weapon is one of a set, and so are Pit Move and Shunt:
  // switching one on switches off the others, since the engine takes one
  // of each set a turn.
  const setOf = (optionId: string) => {
    const option = options.find((o) => o.id === optionId);
    if (option?.turnMode !== true) return undefined;
    const effect = option.effect as OptionEffect;
    return TURN_WEAPONS.has(effect) ? TURN_WEAPONS : PUSH_OPTIONS.has(effect) ? PUSH_OPTIONS : undefined;
  };

  const toggleActivated = (optionId: string) => {
    const set = setOf(optionId);
    onActivatedChange(
      activated.includes(optionId)
        ? activated.filter((id) => id !== optionId)
        : [
          ...(set ? activated.filter((id) => setOf(id) !== set) : activated),
          optionId,
        ],
    );
  };

  // Front is the default rather than a stored value, so an Option turned
  // back to the front drops out of the list entirely and the submission
  // stays as small as what the player actually changed.
  const facingOf = (optionId: string): RelativeFacing =>
    aimed.find((a) => a.optionId === optionId)?.facing ?? 'front';

  const aimAt = (optionId: string, side: RelativeFacing) => {
    const rest = aimed.filter((a) => a.optionId !== optionId);
    onAimedChange(side === 'front' ? rest : [...rest, { optionId, facing: side }]);
  };

  // The Transverse Beam and Shunt are switched on with their side, and
  // off by choosing that side again.
  const setSided = (optionId: string, side: 'left' | 'right' | null) => {
    if (side === null) {
      onActivatedChange(activated.filter((id) => id !== optionId));
      onAimedChange(aimed.filter((a) => a.optionId !== optionId));
      return;
    }
    if (!activated.includes(optionId)) toggleActivated(optionId);
    aimAt(optionId, side);
  };

  // A card in the hand is available unless it is already in a register, or
  // set aside on an Option.
  const placed = new Set(
    [...registers, ...paired.map((p) => p?.card ?? null)]
      .filter((c): c is ProgramCard => !!c).map((c) => c.priority),
  );
  for (const entry of setAside) placed.add(entry.card.priority);
  // Lowest priority first: the U-turns and Back Ups on the left, the Move
  // 3s on the right. A dealt hand arrives in draw order, which is no
  // order at all to choose from -- and it stays that way on the robot,
  // since a sort here is a display choice and the hand as recorded should
  // say what was drawn.
  //
  // Safe to sort: nothing reads the hand in order. Every rule that takes
  // cards out of it -- the auto-fill, a power-down's random program --
  // shuffles first, and everything else looks a card up by priority.
  const sorted = hand.slice().sort((a, b) => a.priority - b.priority);
  const available = sorted.filter((c) => !placed.has(c.priority));

  const toggleGadget = (slot: number, optionId: string) => {
    const next = gadgets.map((g) => g.slice());
    while (next.length < slots.length) next.push([]);

    const already = next[slot].includes(optionId);
    if (already) {
      next[slot] = next[slot].filter((id) => id !== optionId);
      onGadgetsChange(next);
      return;
    }

    // An option with ammo can be programmed into more than one register
    // in a turn, limited by the ammo it has left. Anything else is a
    // one-shot and moves rather than multiplies, so selecting a new
    // register clears the old one.
    const option = options.find((o) => o.id === optionId);
    const ammo = option?.payloadKind === 'ammo' ? (option.remaining ?? 0) : 1;
    if (ammo <= 1) {
      for (let i = 0; i < next.length; i++) {
        next[i] = next[i].filter((id) => id !== optionId);
      }
    } else {
      // Already at the ammo limit: the click does nothing rather than
      // building a program the engine will reject.
      const used = next.reduce((n, ids) => n + (ids.includes(optionId) ? 1 : 0), 0);
      if (used >= ammo) return;
    }

    // ONE gadget per register: a robot deploys one thing at a time, so
    // choosing a second replaces the first rather than joining it.
    next[slot] = [optionId];

    onGadgetsChange(next);
  };

  /** Into a register, or with `second` into its pair's second row. */
  const place = (card: ProgramCard, slot: number, second = false) => {
    if (locked[slot]) return;
    if (slot === 5 && ![0, 1, 2, 3, 4].every(filled)) {
      setRefused({ slot, second, why: 'Program registers 1 to 5 first' });
      setHeld(null);
      return;
    }
    const next = registers.slice();
    const nextPaired = slots.map((i) => paired[i] ?? null);
    // The same card cannot occupy two slots.
    for (let i = 0; i < next.length; i++) {
      if (next[i]?.priority === card.priority) next[i] = null;
      if (nextPaired[i]?.card?.priority === card.priority) {
        nextPaired[i] = { via: nextPaired[i]!.via };
      }
    }
    const pair = nextPaired[slot];
    if (pair) {
      // Each row takes the card dropped on it, replacing the one there.
      // Then the pair sorts itself: the primary -- the higher priority --
      // on top, and a lone card on top too. Refused when the Option does
      // not take the two.
      const cards = (second ? [next[slot], card] : [card, pair.card ?? null])
        .filter((c): c is ProgramCard => !!c)
        .sort((a, b) => b.priority - a.priority);
      const option = options.find((o) => o.id === pair.via)!;
      const why = cards.length === 2 ? pairProblem(option, cards) : undefined;
      if (why) {
        setRefused({ slot, second, why });
        setHeld(null);
        return;
      }
      next[slot] = cards[0];
      nextPaired[slot] = cards[1] ? { via: pair.via, card: cards[1] } : { via: pair.via };
    } else {
      // Placing onto an occupied slot returns the previous card to the
      // hand simply by dropping the reference; `available` recomputes
      // from this.
      next[slot] = card;
    }
    guardSixth(next, nextPaired);
    setRefused(null);
    onChange(next);
    onPairedChange(nextPaired);
    setHeld(null);
  };

  // One card back to the hand. Taking a pair's primary moves the other up.
  const clear = (slot: number, second = false) => {
    if (locked[slot]) return;
    const next = registers.slice();
    next[slot] = second ? registers[slot] : (paired[slot]?.card ?? null);
    const nextPaired = slots.map((i) => (
      i === slot && paired[i]?.card ? { via: paired[i]!.via } : (paired[i] ?? null)
    ));
    guardSixth(next, nextPaired);
    onChange(next);
    if (nextPaired.some((p, i) => p !== (paired[i] ?? null))) onPairedChange(nextPaired);
    setRefused(null);
  };

  // Every card the player placed, back to the hand. Locked registers keep
  // theirs, and a pair keeps its Option with no card in it.
  const anyPlaced = registers.some((c, i) => c && !locked[i]) || paired.some((p) => p?.card);
  const clearAll = () => {
    onChange(registers.map((c, i) => (locked[i] ? c : null)));
    if (paired.some((p) => p?.card)) onPairedChange(slots.map((i) => (paired[i] ? { via: paired[i]!.via } : null)));
    setRefused(null);
    setHeld(null);
  };

  // Only the registers the player can actually fill. A locked register is
  // not theirs to program: it already holds its card, the submission
  // deliberately sends null for it, and the engine rejects any attempt to
  // set one. Requiring a card there meant that if the locked slot arrived
  // empty for any reason the button greyed out with nothing the player
  // could do about it, and the game could not proceed at all. The button
  // should never depend on data the player cannot supply.
  const complete = scrambled || registers.every((_, i) => filled(i));

  // A placed card drags as a hand card does, so it can be dropped in
  // another register; it also says where it came from, so the hand can
  // take it back.
  const dragFromSlot = (e: DragEvent, card: ProgramCard, slot: number, second = false) => {
    e.dataTransfer.setData('application/json', JSON.stringify(card));
    e.dataTransfer.setData(FROM_SLOT, JSON.stringify({ slot, second }));
    e.dataTransfer.effectAllowed = 'move';
    setHeld(card);
  };

  const cardFace = (c: ProgramCard) => (
    <span className="card-face">
      <span className="card-priority num">{c.priority}</span>
      <span className="card-glyph">{CARD_GLYPH[c.type]}</span>
      <span className="card-name">{CARD_LABEL[c.type]}</span>
    </span>
  );

  return (
    <section className="programmer">
      {needsFacing && <FacingPick facing={facing} onPick={onFacingChange} disabled={submitted} />}

      {notices}

      <div
        className="registers"
        role="list"
        style={{ gridTemplateColumns: `repeat(${registers.length}, var(--card-w))` }}
      >
        {registers.map((card, i) => (
          <div
            key={i}
            role="listitem"
            // A submitted register is as fixed as a locked one. Every
            // other control on this screen takes `disabled={submitted}`;
            // a `div` cannot be disabled, so the same guard is written
            // into the handlers by hand -- and without it the cards could
            // be rearranged after Submit, on screen only, while the turn
            // ran the program that was actually sent.
            className={`slot${locked[i] || submitted ? ' locked' : ''}`
              + `${held && !locked[i] && !submitted ? ' target' : ''}${i === 5 ? ' extra' : ''}`}
            title={refused?.slot === i && !refused.second ? refused.why : undefined}
            draggable={!!card && !locked[i] && !submitted}
            onDragStart={(e) => { if (card) dragFromSlot(e, card, i); }}
            onDragEnd={() => setHeld(null)}
            onDragOver={(e) => { if (!locked[i] && !submitted) e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              if (submitted) return;
              const raw = e.dataTransfer.getData('application/json');
              if (raw) place(JSON.parse(raw) as ProgramCard, i);
            }}
            onClick={() => {
              if (submitted) return;
              if (held) place(held, i);
              else if (card && !locked[i]) clear(i);
            }}
          >
            <span className="slot-number">{i + 1}</span>

            {/* A locked register carries a card the player did not put
                there and cannot move, and the only sign of it was the
                slot's own shading. Said here, above the card, the way a
                gadget is said below it. */}
            {locked[i] && <span className="slot-lock">Locked</span>}

            {card ? cardFace(card) : <span className="slot-empty">{scrambled ? 'scrmbl' : 'empty'}</span>}

            {/* Gadgets fire BEFORE this register's card, so a robot can
                drop something and move off it. */}
            {[...(paired[i] ? [paired[i]!.via] : []), ...(gadgets[i] ?? []), ...(moveOptions[i] ?? [])].length > 0 && (
              <span className="slot-gadgets">
                {[...(paired[i] ? [paired[i]!.via] : []), ...(gadgets[i] ?? []), ...(moveOptions[i] ?? [])].map((id) => {
                  const option = options.find((o) => o.id === id);
                  // A move option stays in its register when the card
                  // changes, and shows red while the card there is not
                  // the one it changes.
                  const idle = option !== undefined && isMoveOption(option)
                    && !registerWeapon(option)
                    && !scrambled && !(card && moveOptionChanges(option.effect, card.type));
                  return (
                    <span
                      key={id}
                      className={idle ? 'slot-gadget idle' : 'slot-gadget'}
                      title={idle ? `No effect on this card`
                        : option && OPTION_SLOT_NAME[option.effect] ? option.name : undefined}
                    >
                      {(option && OPTION_SLOT_NAME[option.effect]) ?? option?.name ?? id}
                    </span>
                  );
                })}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* A paired register's second card, under it. The primary -- the
          higher priority, which runs first -- is always the one above. */}
      {paired.some((p) => p) && (
        <div
          className="registers"
          role="list"
          style={{ marginTop: '0.25rem', gridTemplateColumns: `repeat(${registers.length}, var(--card-w))` }}
        >
          {registers.map((_, i) => {
            const second = paired[i]?.card ?? null;
            if (!paired[i]) return <div key={i} aria-hidden="true" />;
            return (
              <div
                key={i}
                role="listitem"
                className={`slot${submitted ? ' locked' : ''}${held && !submitted ? ' target' : ''}`}
                title={refused?.slot === i && refused.second ? refused.why : undefined}
                draggable={!!second && !submitted}
                onDragStart={(e) => { if (second) dragFromSlot(e, second, i, true); }}
                onDragEnd={() => setHeld(null)}
                onDragOver={(e) => { if (!submitted) e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (submitted) return;
                  const raw = e.dataTransfer.getData('application/json');
                  if (raw) place(JSON.parse(raw) as ProgramCard, i, true);
                }}
                onClick={() => {
                  if (submitted) return;
                  if (held) place(held, i, true);
                  else if (second) clear(i, true);
                }}
              >
                {second ? cardFace(second) : <span className="slot-empty">empty</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* A line between the registers and the hand, which a second row
          of registers otherwise runs into. */}
      <div
        className="hand"
        role="list"
        style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--ink)' }}
        // A card dragged out of a register and dropped on the hand goes
        // back, as a click on it does.
        onDragOver={(e) => {
          if (!submitted && e.dataTransfer.types.includes(FROM_SLOT)) e.preventDefault();
        }}
        onDrop={(e) => {
          const raw = e.dataTransfer.getData(FROM_SLOT);
          if (!raw || submitted) return;
          e.preventDefault();
          const from = JSON.parse(raw) as { slot: number; second: boolean };
          clear(from.slot, from.second);
        }}
      >
        {/* A placed card leaves a gap rather than closing up the row, so
            every card stays where it was dealt and one taken back out of a
            register returns to its own place. */}
        {available.length > 0 && sorted.map((card) => (placed.has(card.priority) ? (
          <span key={card.priority} className="card card-gap" aria-hidden="true" />
        ) : (
          <button
            key={card.priority}
            role="listitem"
            className={`card${held?.priority === card.priority ? ' held' : ''}`}
            // The one control on this screen that was missing the guard
            // its siblings all carry. `draggable` is turned off with it:
            // a disabled button still drags, so the card could be dropped
            // into a register the click could no longer reach.
            disabled={submitted}
            draggable={!submitted}
            onDragStart={(e) => {
              e.dataTransfer.setData('application/json', JSON.stringify(card));
              e.dataTransfer.effectAllowed = 'move';
              setHeld(card);
            }}
            onDragEnd={() => setHeld(null)}
            onClick={() => setHeld(held?.priority === card.priority ? null : card)}
            // Straight into the first register with room, so a program
            // built in order is five double clicks rather than ten single
            // ones. The single-click pick-up is untouched and is still
            // what you use to choose the slot.
            //
            // The two clicks that make up the double have already toggled
            // `held` on and off again by the time this runs, which leaves
            // it where `place` wants it anyway.
            onDoubleClick={() => {
              const slot = registers.findIndex((c, i) => c === null && !locked[i]);
              if (slot >= 0) place(card, slot);
            }}
          >
            <span className="card-priority num">{card.priority}</span>
            <span className="card-glyph">{CARD_GLYPH[card.type]}</span>
            <span className="card-name">{CARD_LABEL[card.type]}</span>
          </button>
        )))}
        {available.length === 0 && (
          // A robot on 9 damage is dealt nothing at all and programs
          // nothing: its five locked registers above are the whole turn.
          // "Every card is placed" is true but reads as an accident.
          <p className="muted">
            {hand.length === 0 ? 'No cards this turn.' : 'Every card is placed.'}
          </p>
        )}
      </div>

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button
          className="secondary tiny"
          disabled={submitted || !anyPlaced}
          title="Every card back to the hand"
          onClick={clearAll}
        >
          Clear
        </button>
      </div>

      {/* Directly under the hand, which is what it replaces and what the
          player is looking at when they decide the hand is not worth
          programming. Gone once it has been spent -- once per turn -- so
          the row is its own answer to whether it is still available, and
          off after a submission: "before your robot is programmed".

          Hidden with an empty hand too. On 9 damage there is no hand to
          replace, so the card would draw nothing and spend the point that
          destroys the robot. */}
      {recompile && hand.length > 0 && (
        <div className="gadget-grid">
          <div className="gadget-row">
            <span className="gadget-name" title={recompile.text}>
              {recompile.name}<Charges option={recompile} />
            </span>
            <button
              className="secondary tiny"
              disabled={submitting || submitted}
              title={recompile.text}
              onClick={onRecompile}
            >
              New hand, 1 damage
            </button>
          </div>
        </div>
      )}

      {pairers.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {pairers.map((pairer) => (
              <div key={pairer.id} className="gadget-row">
                <span className="gadget-name" title={optionTooltip(pairer)}>
                  {pairer.name}<Charges option={pairer} />
                </span>
                <span className="row" style={{ gap: '0.2rem' }}>
                  {slots.map((slot) => (
                    <button
                      key={slot}
                      className={paired[slot]?.via === pairer.id ? 'gadget-slot on' : 'gadget-slot'}
                      disabled={locked[slot] || submitted}
                      onClick={() => togglePair(slot, pairer.id)}
                    >
                      {slot + 1}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {launchers.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {launchers.map((launcher) => (
              <div key={launcher.id} className="gadget-row">
                <span className="gadget-name" title={optionTooltip(launcher)}>
                  {launcher.name}<Charges option={launcher} />
                </span>
                <span className="row" style={{ gap: '0.2rem' }}>
                  {slots.map((slot) => (
                    <button
                      key={slot}
                      className={
                        (gadgets[slot] ?? []).includes(launcher.id)
                          ? 'gadget-slot on'
                          : 'gadget-slot'
                      }
                      disabled={locked[slot] || submitted || isOverride(paired[slot]?.via ?? '')
                        || stillVirtual(slot)}
                      title={stillVirtual(slot)
                        ? 'Your robot is still virtual in this register'
                        : virtual
                          ? 'Your robot is virtual: a register it is still virtual for '
                            + 'drops nothing and keeps the payload'
                          : undefined}
                      onClick={() => toggleGadget(slot, launcher.id)}
                    >
                      {slot + 1}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {moveOptionCards.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {moveOptionCards.map((moveOption) => (
              <div key={moveOption.id} className="gadget-row">
                <span className="gadget-name" title={optionTooltip(moveOption)}>
                  {moveOption.name}<Charges option={moveOption} />
                </span>
                <span className="row" style={{ gap: '0.2rem' }}>
                  {/* A locked register's card still runs, so a move option can
                      go on it. */}
                  {slots.map((slot) => (
                    <button
                      key={slot}
                      className={(moveOptions[slot] ?? []).includes(moveOption.id) ? 'gadget-slot on' : 'gadget-slot'}
                      // No manoeuvre changes a pair. The Big Gun changes
                      // what is fired, and goes beside one.
                      disabled={submitted || (!!paired[slot] && !weaponIds.has(moveOption.id))
                        // Nothing after register 5 for the EMP Generator to act on.
                        || (slot >= 4 && NEXT_REGISTER_WEAPONS.has(moveOption.effect as OptionEffect))
                        || (weaponIds.has(moveOption.id) && stillVirtual(slot))}
                      title={weaponIds.has(moveOption.id) && stillVirtual(slot)
                        ? 'Your robot is still virtual in this register'
                        : undefined}
                      onClick={() => toggleMoveOption(slot, moveOption.id)}
                    >
                      {slot + 1}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {switches.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {switches.map((option) => (
              <div key={option.id} className="gadget-row">
                <span className="gadget-name" title={option.text}>
                  {option.name}<Charges option={option} />
                </span>
                {SIDED_TURN_CARDS.has(option.effect as OptionEffect) ? (
                  <span className="row" style={{ gap: '0.2rem' }}>
                    {(['left', 'right'] as const).map((side) => {
                      const on = activated.includes(option.id) && facingOf(option.id) === side;
                      return (
                        <button
                          key={side}
                          className={on ? 'gadget-slot on' : 'gadget-slot'}
                          style={{ width: 'auto', padding: '0 0.6rem' }}
                          disabled={submitted}
                          onClick={() => setSided(option.id, on ? null : side)}
                        >
                          {side === 'left' ? 'Left' : 'Right'}
                        </button>
                      );
                    })}
                  </span>
                ) : bombSet(option) ? (
                  <button
                    className={bombActivated.includes(option.id) ? 'gadget-slot on' : 'gadget-slot'}
                    style={{ width: 'auto', padding: '0 0.6rem' }}
                    disabled
                    title="Switched by the scrambler bomb"
                  >
                    {bombActivated.includes(option.id) ? 'On' : 'Off'}
                  </button>
                ) : (
                  <button
                    className={activated.includes(option.id) ? 'gadget-slot on' : 'gadget-slot'}
                    style={{ width: 'auto', padding: '0 0.6rem' }}
                    disabled={submitted}
                    onClick={() => toggleActivated(option.id)}
                  >
                    {activated.includes(option.id) ? 'On' : 'Off'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {dials.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {dials.map((option) => (
              <div key={option.id} className="gadget-row">
                <span className="gadget-name" title={option.text}>
                  {option.name}<Charges option={option} />
                </span>
                <span className="row" style={{ gap: '0.2rem' }}>
                  {AIM_SIDES.map((side) => (
                    <button
                      key={side}
                      className={
                        (bombSide.get(option.id) ?? facingOf(option.id)) === side ? 'gadget-slot on' : 'gadget-slot'
                      }
                      disabled={submitted || bombSide.has(option.id)}
                      title={bombSide.has(option.id) ? 'Turned by the scrambler bomb' : AIM_LABEL[side]}
                      onClick={() => aimAt(option.id, side)}
                    >
                      {AIM_GLYPH[side]}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {holders.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          {holders.map((holder) => {
            const mine = setAside.find((e) => e.optionId === holder.id)?.card ?? null;
            const heldAlready = holder.heldCard;
            return (
              <div key={holder.id} className="gadget-row">
                <span className="gadget-name" title={holder.text}>{holder.name}<Charges option={holder} /></span>
                <span className="holder-action">
                  {heldAlready ? (
                    <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                      already holding a card
                    </span>
                  ) : mine ? (
                    // The card alone. What the button DOES belongs in the
                    // tooltip: the row is read at a glance for what is on
                    // the Option, and the words outnumbered the card.
                    <button
                      className="secondary tiny"
                      onClick={() => onSetAsideChange(
                        setAside.filter((e) => e.optionId !== holder.id),
                      )}
                      disabled={submitted}
                      title="Take this card back"
                    >
                      {CARD_LABEL[mine.type]} {mine.priority}
                    </button>
                  ) : held ? (
                    // "one of the remaining MOVEMENT cards" for a Flywheel;
                    // the Conditional takes anything. Saying so beats a
                    // greyed button with no explanation.
                    holder.effect === 'flywheel'
                      && !['Move1', 'Move2', 'Move3', 'BackUp'].includes(held.type) ? (
                        <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                          the flywheel takes a movement card, not {CARD_LABEL[held.type]}
                        </span>
                      ) : (
                        <button
                          className="tiny"
                          onClick={() => {
                            onSetAsideChange([
                              ...setAside.filter((e) => e.optionId !== holder.id),
                              { optionId: holder.id, card: held },
                            ]);
                            setHeld(null);
                          }}
                          disabled={submitted}
                        >
                          Put {CARD_LABEL[held.type]} here
                        </button>
                      )
                  ) : (
                    <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                      pick a{holder.effect === 'flywheel' ? ' movement' : ''} card, then click here
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Power down is decided here rather than on a screen of its own.
          The engine still runs it as a separate step after programming;
          this is only where the answer is collected. The same control
          appears on that step for a turn whose program was forced
          through, so it lives in one place. */}
      <PowerDownChoice
        value={powerDown}
        onChange={onPowerDownChange}
        disabled={submitted}
      />

      {/* Options gained since the last time this player programmed. The
          log records them, but the log scrolls and a new Option changes
          how the turn should be played, so it is worth saying here where
          the player is already looking. */}
      {gained.length > 0 && (
        <p className="notice ok upgrade">
          {gained.map((g, i) => (
            <span key={`${g.kind}-${g.name}`}>
              {i > 0 && <br />}
              {g.kind === 'swapped' ? 'You have just swapped to: '
                : g.kind === 'reloaded' ? 'You have just reloaded: '
                  : 'You have just upgraded to: '}
              <strong>{g.name}</strong>
            </span>
          ))}
        </p>
      )}

      <button onClick={onSubmit} disabled={!complete || submitting || submitted}>
        {submitted ? 'Programmed' : 'Submit Program'}
      </button>
      {submitted && (
        <button
          onClick={onUnsubmit}
          disabled={submitting}
          title={'Take it back and change it. The cards stay where they are,'
            + ' and are still what plays if your time runs out.'}
        >
          Unsubmit
        </button>
      )}
    </section>
  );
}
